import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { usePhotoSeriesGet } from "hooks/citajteo/photo_series/usePhotoSeriesGet";
import { usePhotoSeriesUpdate } from "hooks/citajteo/photo_series/usePhotoSeriesUpdate";
import { QUERY_KEYS } from "constant/react_query";
import toast from "react-hot-toast";
import { PhotoSeries } from "model/photo_series/PhotoSeries";
import { useForm } from "react-hook-form";
import PhotoSeriesForm, {
  PHOTO_SERIES_EMPTY_FORM,
  PhotoSeriesFormState,
} from "components/admin/common/photo_series/PhotoSeriesForm/PhotoSeriesForm";
import {
  PHOTO_SERIES_TYPES,
  PhotoSeriesType,
} from "constant/photo_series/photo_series_type.constant";
import PhotoSeriesEditNav from "components/admin/common/photo_series/nav/PhotoSeriesEditNav";

const buildFormFromPhotoSeries = (
  photoSeries: PhotoSeries,
): PhotoSeriesFormState => ({
  name: photoSeries.name ?? "",
  slug: photoSeries.slug ?? "",
  desc: photoSeries.desc ?? "",
  group: photoSeries.group ?? "",
  sType: photoSeries.sType ?? PHOTO_SERIES_TYPES.FOTOGRAFIJE,
});

const PhotoSeriesEdit: React.FC = () => {
  const { itemId = "" } = useParams<{ itemId?: string }>();
  const photoSeries = usePhotoSeriesGet(itemId);
  const { photoSeriesUpdateMutate } = usePhotoSeriesUpdate({
    invalidateKeysArray: [
      [QUERY_KEYS.CITAJTEO_PHOTO_SERIES_SEARCH],
      [QUERY_KEYS.CITAJTEO_PHOTO_SERIES_GET, itemId],
    ],
  });

  const {
    control,
    handleSubmit: handleFormSubmit,
    reset,
  } = useForm<PhotoSeriesFormState>({
    defaultValues: PHOTO_SERIES_EMPTY_FORM,
  });

  const [errorText, setErrorText] = useState("");

  useEffect(() => {
    if (photoSeries) {
      reset(buildFormFromPhotoSeries(photoSeries));
    }
  }, [photoSeries, reset]);

  const handleSubmit = (form: PhotoSeriesFormState) => {
    setErrorText("");

    const trimmedName = form.name.trim();
    const trimmedSlug = form.slug.trim();
    const trimmedGroup = form.group.trim();
    const sType = form.sType as PhotoSeriesType;
    const trimmedDesc = form.desc.trim();

    if (trimmedName === "") {
      setErrorText("Naziv ne sme biti prazan.");
      return;
    }

    if (trimmedGroup === "") {
      setErrorText("Grupa ne sme biti prazna.");
      return;
    }

    const updatePayload = {
      name: trimmedName,
      slug: trimmedSlug,
      desc: trimmedDesc,
      group: trimmedGroup,
      sType,
    };

    photoSeriesUpdateMutate(
      { id: itemId, updatePayload },
      {
        onSuccess: () => {
          toast.success("Uspešno ažuriranje foto serije.");
        },
        onError: (error) => {
          console.error("Error updating photo series:", error);
          toast.error("Greška prilikom ažuriranja foto serije.");
        },
      },
    );
  };

  const handleReset = () => {
    if (photoSeries) {
      reset(buildFormFromPhotoSeries(photoSeries));
    } else {
      reset(PHOTO_SERIES_EMPTY_FORM);
    }
    setErrorText("");
  };

  if (!photoSeries) {
    return <Div>Učitavanje foto serije...</Div>;
  }

  return (
    <Div>
      {photoSeries && <PhotoSeriesEditNav photoSer={photoSeries} />}
      <div>
        <PhotoSeriesForm
          control={control}
          errorText={errorText}
          isEditMode={true}
          onSubmit={handleFormSubmit(handleSubmit)}
          onReset={handleReset}
        />
      </div>
    </Div>
  );
};

export default PhotoSeriesEdit;

const Div = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;
